import { SEVERITY, type PendingWorkItem, type WorkStatusKind } from '../../../../shared/lib/workStatus';
import type { AttentionItem, AttentionReason, BudgetAlertItem } from '../../../../shared/types/analytics';

/**
 * UX-03 — the Today inbox's merge layer (lib/attention/inbox.ts).
 *
 * Pure functions over the three existing data sources (pending work,
 * analytics attention, budget alerts) that Today, the epic banner and the
 * Reliability tab each used to render separately. `buildInbox` is the only
 * export the rest of the app needs; everything else here is an
 * implementation detail kept exported for the unit tests.
 */

export type InboxCategory = 'needs_human' | 'review' | 'failing' | 'budget';

export interface InboxItem {
  id: string;
  category: InboxCategory;
  /** Lower is more urgent — same 0-8 scale as workStatus.ts's SEVERITY. */
  severity: number;
  epicRef?: string;
  repo?: string;
  epicNumber?: number;
  storyId?: string | null;
  title: string;
  subtitle: string;
  reason?: string | null;
  runbook?: string | null;
  /**
   * Attention reasons folded into a pending-work item during dedupe (e.g. a
   * pending "blocked" story that /analytics/attention *also* flags
   * high_retries). Not part of the spec's InboxItem field table, but the
   * spec explicitly asks for the pending item to get "any attention reasons
   * merged in as extra chips" — this is where those land. Empty/omitted
   * when nothing was merged.
   */
  extraReasons?: string[];
  updatedAt: string;
  ageMinutes: number;
  href: string;
}

export interface BuildInboxArgs {
  pending: PendingWorkItem[];
  attention: AttentionItem[];
  budget: BudgetAlertItem[];
  now: number;
  includeOlder: boolean;
}

export interface BuildInboxResult {
  items: InboxItem[];
  hiddenOlderCount: number;
}

const DAY_MIN = 24 * 60;

/**
 * Pending statuses this inbox ever surfaces — the same SHOWN set
 * PendingWorkBanner uses. `queued` and `awaiting_approval` are healthy
 * (queued is normal, PLAN_POSTED already renders its own review card) and
 * `running` never appears in /activity/pending at all.
 */
const PENDING_SHOWN: ReadonlySet<WorkStatusKind> = new Set([
  'operator_action_required', 'escalated', 'crashed_at_startup',
  'launch_failed', 'blocked', 'stalled',
]);

const NEEDS_HUMAN_PENDING: ReadonlySet<WorkStatusKind> = new Set(['operator_action_required', 'escalated']);

/**
 * EpicState values that mean "a human is reviewing this" — the epic reached
 * READY_FOR_REVIEW(_NOTIFIED), or is parked on the requirements-review gate.
 * A `stuck` attention item in any other state is a plain stall, not a
 * review wait, so it goes to `failing` instead.
 */
const REVIEW_STATES: ReadonlySet<string> = new Set([
  'READY_FOR_REVIEW', 'READY_FOR_REVIEW_NOTIFIED', 'REQUIREMENTS_REVIEW',
]);

/** Same rule as PendingWorkBanner's isDependencyWait: a stalled story still
 *  in PENDING state hasn't started — it's waiting on an upstream dependency,
 *  not genuinely stuck, so it never appears in the inbox. */
function isDependencyWait(it: PendingWorkItem): boolean {
  return it.status === 'stalled' && it.state === 'PENDING';
}

function dedupeKey(repo: string, epicNumber: number, storyId?: string | null): string {
  return `${repo}#${epicNumber}#${storyId ?? ''}`;
}

/**
 * AttentionReason has no native severity scale — /analytics/attention is a
 * different backend classifier from the pending-work one in workStatus.ts.
 * Map each reason onto the nearest WorkStatusKind rung so attention-derived
 * items sort sensibly alongside pending-work ones on the same 0-8 scale.
 */
const ATTENTION_SEVERITY: Record<AttentionReason, number> = {
  escalated: SEVERITY.escalated,        // 1
  crashed: SEVERITY.crashed_at_startup, // 2
  high_retries: SEVERITY.launch_failed, // 3
  blocked: SEVERITY.blocked,            // 4
  stuck: SEVERITY.stalled,              // 5 (review or plain stall)
};

const ATTENTION_REASON_LABEL: Record<AttentionReason, string> = {
  stuck: 'stuck',
  escalated: 'escalated',
  blocked: 'blocked',
  high_retries: 'high retries',
  crashed: 'crashed',
};

function pendingHref(it: PendingWorkItem): string {
  return `/epics/${encodeURIComponent(it.epicRef)}`;
}

/**
 * AttentionItem carries no epicRef, only repo + epicNumber. The removed
 * Health AttentionSection (pre-UX-02) linked with the plain epic number
 * (`/epics/${epicNumber}`), which the `/epics/:id` route already resolves
 * as a fallback alongside the encoded epicRef — same precedent here.
 */
function attentionHref(it: AttentionItem): string {
  // Bare numbers only resolve on legacy single-repo installs; prefer the ref.
  return `/epics/${it.epicRef ? encodeURIComponent(it.epicRef) : it.epicNumber}`;
}

function attentionStoryId(it: AttentionItem): string | null {
  return it.retryDetail?.storyId ?? it.crashDetail?.storyId ?? null;
}

function pendingSubtitle(it: PendingWorkItem): string {
  return it.storyId ? `${it.repo} · story ${it.storyId}` : it.repo;
}

function attentionSubtitle(it: AttentionItem, storyId: string | null): string {
  return storyId ? `${it.repo} · story ${storyId}` : it.repo;
}

function budgetTitle(it: BudgetAlertItem): string {
  if (it.kind === 'over_budget') return `${it.projectId} is over budget`;
  const pct = it.capUsd ? Math.round((it.spendUsd / it.capUsd) * 100) : null;
  return pct != null
    ? `${it.projectId} reached ${pct}% of monthly budget`
    : `${it.projectId} is nearing its monthly budget`;
}

function fromPending(it: PendingWorkItem): InboxItem | null {
  if (!PENDING_SHOWN.has(it.status)) return null;
  if (isDependencyWait(it)) return null;

  const category: InboxCategory = NEEDS_HUMAN_PENDING.has(it.status)
    ? 'needs_human'
    : 'failing'; // crashed_at_startup, launch_failed, blocked, stalled (non-dependency)

  return {
    id: it.id,
    category,
    severity: SEVERITY[it.status],
    epicRef: it.epicRef,
    repo: it.repo,
    epicNumber: it.epicNumber,
    storyId: it.storyId,
    title: `#${it.epicNumber} ${it.title}`,
    subtitle: pendingSubtitle(it),
    reason: it.reason,
    runbook: it.runbook,
    updatedAt: it.updatedAt,
    ageMinutes: it.minutesInState,
    href: pendingHref(it),
  };
}

function fromAttention(it: AttentionItem): InboxItem | null {
  const storyId = attentionStoryId(it);
  let category: InboxCategory;
  if (it.reason === 'escalated') category = 'needs_human';
  else if (it.reason === 'stuck') category = REVIEW_STATES.has(it.state) ? 'review' : 'failing';
  else category = 'failing'; // blocked, high_retries, crashed

  return {
    id: it.id,
    category,
    severity: ATTENTION_SEVERITY[it.reason],
    repo: it.repo,
    epicNumber: it.epicNumber,
    storyId,
    title: `#${it.epicNumber} ${it.title}`,
    subtitle: attentionSubtitle(it, storyId),
    reason: ATTENTION_REASON_LABEL[it.reason],
    runbook: null,
    updatedAt: it.updatedAt,
    ageMinutes: it.minutesInState,
    href: attentionHref(it),
  };
}

function fromBudget(it: BudgetAlertItem): InboxItem {
  return {
    id: it.id,
    category: 'budget',
    severity: it.severity === 'critical' ? 1 : 6,
    title: budgetTitle(it),
    subtitle: it.reason,
    reason: it.reason,
    runbook: null,
    // Budget alerts carry no timestamp of their own (they're a point-in-time
    // read of the current month's spend, not an event) — treat as always
    // fresh so the 24h cutoff never hides one.
    updatedAt: '',
    ageMinutes: 0,
    href: '/insights?tab=reliability',
  };
}

export function buildInbox(args: BuildInboxArgs): BuildInboxResult {
  const { pending, attention, budget, includeOlder } = args;

  // Keyed by a source-namespaced id (never collides across pending / attention
  // / budget) so multiple distinct pending-work items on the SAME epic (e.g.
  // an epic can carry both an operator-action item and a separately-stalled
  // story at once) never overwrite each other. The dedupeKey (repo#epic#story)
  // is only used below to find an attention item's *matching* pending item(s).
  const winners = new Map<string, InboxItem>();
  const pendingByKey = new Map<string, InboxItem[]>();
  const extras = new Map<string, string[]>();

  for (const p of pending) {
    const item = fromPending(p);
    if (!item) continue;
    const id = `pending:${item.id}`;
    winners.set(id, item);
    const key = dedupeKey(p.repo, p.epicNumber, p.storyId);
    const list = pendingByKey.get(key) ?? [];
    list.push(item);
    pendingByKey.set(key, list);
    extras.set(id, []); // placeholder so the id is stable below
  }

  for (const a of attention) {
    const item = fromAttention(a);
    if (!item) continue;
    const key = dedupeKey(a.repo, a.epicNumber, attentionStoryId(a));
    const matches = pendingByKey.get(key);
    if (matches && matches.length > 0) {
      // One or more pending items already cover this epic/story — fold this
      // attention reason in as an extra chip on each, instead of a separate
      // row (the pending item "wins": it carries reason/runbook).
      const label = ATTENTION_REASON_LABEL[a.reason];
      for (const m of matches) {
        const id = `pending:${m.id}`;
        const list = extras.get(id) ?? [];
        if (!list.includes(label)) list.push(label);
        extras.set(id, list);
      }
    } else {
      winners.set(`attention:${item.id}`, item);
    }
  }

  for (const b of budget) {
    // Budget items never collide with epic-scoped ones — namespaced too.
    const item = fromBudget(b);
    winners.set(`budget:${item.id}`, item);
  }

  for (const [id, list] of extras) {
    if (list.length === 0) continue;
    const item = winners.get(id);
    if (item) item.extraReasons = list;
  }

  const all = [...winners.values()];
  const inWindow = includeOlder ? all : all.filter((it) => it.ageMinutes <= DAY_MIN);
  const hiddenOlderCount = includeOlder ? 0 : all.length - inWindow.length;

  inWindow.sort((a, b) => a.severity - b.severity || b.ageMinutes - a.ageMinutes);

  return { items: inWindow, hiddenOlderCount };
}
